import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, ChevronRight, Sparkles, Flame,
  EggFried, Beef, Pizza, Wheat, Salad, CupSoda, CakeSlice,
} from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { HeroBanner } from "@/components/HeroBanner";
import { CategoryNav } from "@/components/CategoryNav";
import { ProductCard, PopularCard } from "@/components/ProductCard";
import { QuickPicks } from "@/components/QuickPicks";
import { Combos } from "@/components/Combos";
import { KitchenStatus } from "@/components/KitchenStatus";
import { ValueCards } from "@/components/ValueCards";
import { PoweredByFudiTap } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { categories, getFeaturedProducts, getProductsByCategory, products } from "@/data/menu";
import { QUICK_PICKS, DECISION_INTENTS } from "@/data/productMeta";
import { restaurantConfig, isFullMenuMode } from "@/config/restaurantConfig";
import { localize } from "@/lib/format";
import CompanionHome from "@/pages/CompanionHome";

export default function HomePage() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  if (!isFullMenuMode()) return <CompanionHome />;
  const popular = getFeaturedProducts(restaurantConfig.featuredIds);
  const recommended = products.filter((p) => p.available && !p.popular).slice(0, 4);
  const hour = new Date().getHours();
  const popularTitle = hour < 11 ? t("greetingMorning") : hour >= 18 ? t("greetingEvening") : t("popular");
  const decisionItems = DECISION_INTENTS.map((d) => ({ id: d.id, key: d.key, tag: d.tags[0] }));

  const goMenu = (catId) => navigate(catId ? `/menu?cat=${catId}` : "/menu");

  return (
    <div data-testid="home-page">
      <TopBar />

      <div className="space-y-7 py-5">
        {/* Search (routes to menu) */}
        <button
          onClick={() => navigate("/menu")}
          className="mx-4 flex h-12 w-[calc(100%-2rem)] items-center gap-3 rounded-full border border-border bg-card px-4 text-start text-muted-foreground shadow-soft active:scale-[0.99]"
          data-testid="home-search-trigger"
        >
          <Search className="h-5 w-5" />
          <span className="text-[15px]">{t("searchPlaceholder")}</span>
        </button>

        <HeroBanner />

        <KitchenStatus />

        <QuickPicks title={t("quickPicksTitle")} items={QUICK_PICKS} />

        {/* Categories */}
        <section>
          <SectionHeader title={t("categories")} />
          <div className="no-scrollbar flex gap-3 overflow-x-auto px-4 pb-1">
            {categories.map((cat, i) => (
              <motion.button
                key={cat.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => goMenu(cat.id)}
                className="flex w-[84px] shrink-0 flex-col items-center gap-2"
                data-testid={`home-category-${cat.slug}`}
              >
                <span className="grid h-[72px] w-[72px] place-items-center rounded-2xl border border-border bg-card shadow-soft">
                  <CategoryEmoji slug={cat.slug} />
                </span>
                <span className="text-center text-xs font-semibold leading-tight">{localize(cat.name, lang)}</span>
              </motion.button>
            ))}
          </div>
        </section>

        {/* Popular (contextual greeting) */}
        <section>
          <SectionHeader
            title={popularTitle}
            icon={<Flame className="h-4 w-4 text-amber-500" />}
            onSeeAll={() => goMenu()}
            seeAllLabel={t("allCategories")}
          />
          <div className="no-scrollbar flex gap-4 overflow-x-auto px-4 pb-1">
            {popular.map((p, i) => (
              <PopularCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>

        {/* Decision helper */}
        <QuickPicks title={t("decisionTitle")} items={decisionItems} testId="decision-helper" />

        {/* Combos & deals */}
        <Combos />

        {/* Recommended */}
        <section className="px-4">
          <SectionHeader
            title={t("recommended")}
            icon={<Sparkles className="h-4 w-4 text-primary" />}
            noPad
          />
          <div className="space-y-3">
            {recommended.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>

        {/* Menu categories preview */}
        {categories.map((cat) => {
          const items = getProductsByCategory(cat.id).slice(0, 3);
          if (!items.length) return null;
          return (
            <section key={cat.id} className="px-4">
              <SectionHeader
                title={localize(cat.name, lang)}
                onSeeAll={() => goMenu(cat.id)}
                seeAllLabel=""
                noPad
              />
              <div className="space-y-3">
                {items.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            </section>
          );
        })}

        <ValueCards />

        <PoweredByFudiTap />
      </div>
    </div>
  );
}

function SectionHeader({ title, icon, onSeeAll, seeAllLabel, noPad }) {
  return (
    <div className={`mb-3 flex items-center justify-between ${noPad ? "" : "px-4"}`}>
      <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold">
        {icon}
        {title}
      </h2>
      {onSeeAll && (
        <button
          onClick={onSeeAll}
          className="flex items-center gap-0.5 text-sm font-semibold text-primary active:scale-95"
          data-testid={`see-all-${title}`}
        >
          {seeAllLabel || ""}
          <ChevronRight className="h-4 w-4 rtl:rotate-180" />
        </button>
      )}
    </div>
  );
}

const CAT_ICON = {
  kahvalti: EggFried,
  burgerler: Beef,
  pizzalar: Pizza,
  makarnalar: Wheat,
  salatalar: Salad,
  icecekler: CupSoda,
  tatlilar: CakeSlice,
};
function CategoryEmoji({ slug }) {
  const Icon = CAT_ICON[slug] || Salad;
  return (
    <span className="grid h-full w-full place-items-center rounded-2xl bg-primary/10 text-primary">
      <Icon className="h-8 w-8" strokeWidth={1.8} />
    </span>
  );
}
